import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {parseISO} from 'date-fns'

import styles from './IterationItem.module.css'

type IterationItemProps = {
  startDate: string
  durationInDays: number
  titleHTML: string
}

export function IterationItem({startDate, durationInDays, titleHTML}: IterationItemProps) {
  const now = new Date()
  const iterationStartDate = parseISO(startDate)
  const endDate = getIterationEndDate(iterationStartDate, durationInDays)
  const isCurrent = iterationStartDate <= now && now <= endDate
  const formatDate = (d: Date) => d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})

  return (
    <div className={styles.IterationItemContainer}>
      <div className={styles.iterationTitleContainer}>
        <VerifiedHTMLText
          // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
          html={titleHTML as SafeHTMLString}
        />
      </div>
      <>&#8226; </>
      <span className={styles.iterationDateRange}>
        {`${formatDate(iterationStartDate)} - ${formatDate(endDate)}`} {isCurrent && <>&#8226; </>}
      </span>
      {isCurrent && <span className={styles.currentIndicator}>Current</span>}
    </div>
  )
}

// The date that an iteration ends. The end date is computed by adding
// the duration (number of days) to the start date, and then subtracting 1.
// We subtract 1 because the end date is inclusive, so we don't actually want
// to include the full duration in the range.
// For example a for an iterationStartDate of Jan 1, and a duration of 2 days,
// the end date should be Jan 2, _not_ Jan 3.
function getIterationEndDate(iterationStartDate: Date, durationInDays: number) {
  return new Date(iterationStartDate.getTime() + (durationInDays - 1) * 24 * 60 * 60 * 1000)
}
